import React, { useState } from 'react';
import { Trophy, Settings, LayoutList, ShieldCheck, Shield } from 'lucide-react';
import { Player, Team, Match, Tournament, UserProfile, Config, RankingConfig } from '../../types';
import { AdminTorneos } from './AdminTorneos';
import { AdminResultados, AdminCategoryValidationSection } from './AdminResultados';
import { AdminRankingCircuito } from './AdminRankingCircuito';

interface OrganizerDashboardProps {
  userId: string;
  userProfile: UserProfile;
  players: Player[];
  teams: Team[];
  matches: Match[];
  tournaments: Tournament[];
  sedesPublicas: UserProfile[];
  allUsers: UserProfile[];
  config: Config;
  rankingConfig: RankingConfig;
  db: any;
  getCollectionPath: (col: string) => string;
}

export function OrganizerDashboard({
  userId,
  userProfile,
  players,
  teams,
  matches,
  tournaments,
  sedesPublicas,
  allUsers,
  config,
  rankingConfig,
  db,
  getCollectionPath
}: OrganizerDashboardProps) {
  const [activeTab, setActiveTab] = useState<'mis_torneos' | 'partidos_resultados' | 'disputas' | 'validaciones_categorias' | 'ranking' | 'marca'>('mis_torneos');

  const myTournaments = tournaments.filter(t => t.organizerId === userId);
  const myMatches = matches.filter(m => m.organizerId === userId || myTournaments.some(t => t.id === m.tournamentId));
  const pendingDisputesCount = myMatches.filter(m => m.status === 'disputed').length;

  React.useEffect(() => {
    const handleTabChange = (e: CustomEvent) => {
      const { tab } = e.detail;
      if (tab === 'torneos' || tab === 'mis_torneos') setActiveTab('mis_torneos');
      if (tab === 'resultados' || tab === 'partidos_resultados') setActiveTab('partidos_resultados');
      if (tab === 'marca') setActiveTab('marca');
    };
    window.addEventListener('changeAdminTab', handleTabChange as EventListener);
    
    const handleNavigateToDisputas = (e: any) => {
      setActiveTab('disputas');
    };
    window.addEventListener('navigateToDisputas', handleNavigateToDisputas);
    
    const handleNavigateToResultados = (e: any) => {
      setActiveTab('partidos_resultados');
    };
    window.addEventListener('navigateToResultados', handleNavigateToResultados);

    return () => {
      window.removeEventListener('changeAdminTab', handleTabChange as EventListener);
      window.removeEventListener('navigateToDisputas', handleNavigateToDisputas);
      window.removeEventListener('navigateToResultados', handleNavigateToResultados);
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="max-w-3xl mx-auto bg-[#1a1c23] rounded-2xl shadow-2xl relative overflow-hidden border border-[#2d313f] flex flex-col mb-8">
        <div className="p-6 md:p-8 relative z-10 flex flex-row items-center justify-center gap-4 md:gap-6">
          <div className="w-16 h-16 md:w-24 md:h-24 rounded-xl flex items-center justify-center p-0 overflow-hidden shrink-0">
             {(userProfile as any).organizerProfile?.logoUrl ? (
               <img src={(userProfile as any).organizerProfile.logoUrl} alt="Logo Organizador" className="w-full h-full object-contain drop-shadow-md" />
             ) : (
               <Trophy size={32} className="opacity-20 text-theme-primary" />
             )}
          </div>
          <div className="text-left min-w-0">
            <h2 className="text-lg md:text-2xl font-black uppercase tracking-wider text-white mb-1 md:mb-2 truncate">
              {(userProfile as any).organizerProfile?.name || 'Mi Marca'}
            </h2>
            <p className="text-[10px] md:text-sm text-zinc-400 font-bold uppercase tracking-widest truncate">Panel de Organizador</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {/* Navigation Tabs (Admin Style) */}
        <div className="flex justify-center mb-8 w-full border-b border-white/5 pb-0 relative z-20 md:pt-4">
          <div className="flex gap-6 w-full sm:w-auto overflow-x-auto custom-scrollbar px-4">
            <button
              onClick={() => setActiveTab('mis_torneos')}
              className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                activeTab === 'mis_torneos'
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
              }`}
            >
              <Trophy className={`w-3.5 h-3.5 md:w-4 md:h-4 ${activeTab === 'mis_torneos' ? 'text-theme-primary' : 'opacity-70'}`} />
              Mis Torneos
            </button>
            <button
              onClick={() => setActiveTab('partidos_resultados')}
              className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                activeTab === 'partidos_resultados'
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
              }`}
            >
              <LayoutList className={`w-3.5 h-3.5 md:w-4 md:h-4 ${activeTab === 'partidos_resultados' ? 'text-theme-primary' : 'opacity-70'}`} />
              Resultados
            </button>
            <button
              onClick={() => setActiveTab('disputas')}
              className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                activeTab === 'disputas'
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
              }`}
            >
              <ShieldCheck className={`w-3.5 h-3.5 md:w-4 md:h-4 ${activeTab === 'disputas' ? 'text-theme-primary' : 'opacity-70'}`} />
              <div className="flex items-center gap-1.5">
                Disputas
                {pendingDisputesCount > 0 && (
                  <span className="bg-red-500 text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(239,68,68,0.5)] border border-red-400 animate-pulse">
                    {pendingDisputesCount}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('validaciones_categorias')}
              className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                activeTab === 'validaciones_categorias'
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
              }`}
            >
              <Shield className={`w-3.5 h-3.5 md:w-4 md:h-4 ${activeTab === 'validaciones_categorias' ? 'text-theme-primary' : 'opacity-70'}`} />
              Validación Categorías
            </button>
            <button
              onClick={() => setActiveTab('ranking')}
              className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                activeTab === 'ranking'
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
              }`}
            >
              <Trophy className={`w-3.5 h-3.5 md:w-4 md:h-4 ${activeTab === 'ranking' ? 'text-theme-primary' : 'opacity-70'}`} />
              Ranking
            </button>
            <button
              onClick={() => setActiveTab('marca')}
              className={`flex items-center justify-center gap-2 pb-3 text-[10px] md:text-[11px] font-semibold uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none whitespace-nowrap border-b-2 ${
                activeTab === 'marca'
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-white/50 hover:text-white/80 hover:border-white/20'
              }`}
            >
              <Settings className={`w-3.5 h-3.5 md:w-4 md:h-4 ${activeTab === 'marca' ? 'text-theme-primary' : 'opacity-70'}`} />
              Mi Marca
            </button>
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === 'mis_torneos' && (
            <AdminTorneos
              organizerId={userId}
              userRole="gestor_torneos"
              tournaments={myTournaments}
              config={config}
              rankingConfig={rankingConfig}
              allTeams={teams}
              players={players}
              sedesPublicas={sedesPublicas}
              db={db}
              clubs={[]}
              matches={myMatches}
              handleShareCanvas={() => {}}
              onPlayerClick={() => {}}
              getCollectionPath={getCollectionPath}
              isOrganizerMode={true}
            />
          )}
          
          {activeTab === 'partidos_resultados' && (
            <div className="h-full relative overflow-y-auto custom-scrollbar pr-2 pb-20">
              <AdminResultados
                players={players}
                sedesPublicas={sedesPublicas}
                config={config}
                db={db}
                getCollectionPath={getCollectionPath}
                userId={userId}
                userRole="gestor_torneos"
                mode="tournaments_only"
                isOrganizerMode={true}
              />
            </div>
          )}

          {activeTab === 'disputas' && (
            <div className="h-full relative overflow-y-auto custom-scrollbar pr-2 pb-20">
              <AdminResultados
                players={players}
                sedesPublicas={sedesPublicas}
                config={config}
                db={db}
                getCollectionPath={getCollectionPath}
                userId={userId}
                userRole="gestor_torneos"
                mode="validations_disputes"
                isOrganizerMode={true}
              />
            </div>
          )}

          {activeTab === 'validaciones_categorias' && (
            <div className="h-full relative overflow-y-auto custom-scrollbar">
              <AdminCategoryValidationSection
                players={players}
                db={db}
                getCollectionPath={getCollectionPath}
                userId={userId}
                userRole="gestor_torneos"
                sedesPublicas={sedesPublicas}
                setLocalSuccessMsg={() => {}}
              />
            </div>
          )}

          {activeTab === 'ranking' && (
            <AdminRankingCircuito
              userId={userId}
              players={players}
              db={db}
              getCollectionPath={getCollectionPath}
              config={config}
              logoUrl={(userProfile as any).organizerProfile?.logoUrl}
            />
          )}

          {activeTab === 'marca' && (
            <OrganizerMarcaForm
              userId={userId}
              userProfile={userProfile}
              db={db}
              getCollectionPath={getCollectionPath}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function OrganizerMarcaForm({ userId, userProfile, db, getCollectionPath }: { userId: string, userProfile: UserProfile, db: any, getCollectionPath: any }) {
  const [name, setName] = useState((userProfile as any).organizerProfile?.name || '');
  const [instagram, setInstagram] = useState((userProfile as any).organizerProfile?.instagram || '');
  const [logoUrl, setLogoUrl] = useState((userProfile as any).organizerProfile?.logoUrl || '');
  const [isLoading, setIsLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const { resizeImage } = await import('../../lib/utils');
      setIsLoading(true);
      resizeImage(file, (compressed) => {
        setLogoUrl(compressed);
        setIsLoading(false);
      });
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      let finalPhotoUrl = logoUrl;
      if (finalPhotoUrl.startsWith('data:image/')) {
        const { ref, uploadString, getDownloadURL } = await import('firebase/storage');
        const { storage } = await import('../../lib/firebase');
        const imageRef = ref(storage, `organizers/${userId}_${Date.now()}.jpg`);
        await uploadString(imageRef, finalPhotoUrl, 'data_url');
        finalPhotoUrl = await getDownloadURL(imageRef);
      }

      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, getCollectionPath('players'), userId), {
        organizerProfile: {
          name,
          instagram,
          logoUrl: finalPhotoUrl
        }
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving organizer profile', err);
      alert('Error al guardar los datos.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#1a1c23] rounded-2xl border border-[#2d313f] p-6 max-w-2xl mx-auto space-y-6">
      <h3 className="text-xl font-black uppercase tracking-wider text-white mb-6">Personalizar Mi Marca</h3>
      
      <div className="space-y-4">
        <div>
          <label className="text-xs opacity-60 font-bold uppercase block mb-2">Logo de la Productora / Torneo</label>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden relative group">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Trophy size={32} className="opacity-20" />
              )}
              <label className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
                <span className="text-[10px] font-bold text-white uppercase mt-1">Cambiar</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
              </label>
            </div>
            <div className="flex-1 text-xs opacity-50">
              Usa un logo con fondo transparente o color sólido para que se vea bien en los banners.
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs opacity-60 font-bold uppercase block mb-2">Nombre Comercial</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej: Padel Tour Pro"
            className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-sm outline-none focus:theme-primary-border text-white placeholder:opacity-30"
          />
        </div>

        <div>
          <label className="text-xs opacity-60 font-bold uppercase block mb-2">Instagram (Opcional)</label>
          <input
            value={instagram}
            onChange={(e) => setInstagram(e.target.value)}
            placeholder="Ej: @mipadeltour"
            className="w-full bg-black/40 border border-white/10 rounded-xl p-3 text-sm outline-none focus:theme-primary-border text-white placeholder:opacity-30"
          />
        </div>

        <div className="pt-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={isLoading || !name.trim()}
            className="theme-primary-bg text-black px-6 py-3 rounded-xl font-black uppercase tracking-widest text-xs hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {isLoading ? 'Guardando...' : saved ? '¡Guardado!' : 'Guardar Cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}
